import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ECI_ORGANIZATION_GROUPS } from '../../data/aiDevelopment/eciModelScores';
import { getContainedTooltipPosition } from './TimeSeriesChart';
import { getSvgPointInPlot } from './modelReleaseFrontierUtils';

const WIDTH = 1280;
const HEIGHT = 600;
const PAD = { top: 54, right: 168, bottom: 72, left: 68 };
const HIT_RADIUS = 8;
const TOOLTIP_CLOSE_DELAY = 80;
const RANGE_YEARS = { '1Y': 1, '3Y': 3, '5Y': 5 };

const dateValue = (value) => new Date(`${value}T00:00:00Z`).getTime();
const formatScore = (value) => value.toFixed(1);

const rangeStart = (range, end, fallback) => {
    if (!RANGE_YEARS[range]) return fallback;
    const start = new Date(`${end}T00:00:00Z`);
    start.setUTCFullYear(start.getUTCFullYear() - RANGE_YEARS[range]);
    return start.toISOString().slice(0, 10);
};

const getAxisTicks = (minimum, maximum) => {
    const first = Math.ceil(minimum / 10) * 10;
    const last = Math.floor(maximum / 10) * 10;
    const ticks = [];
    for (let value = first; value <= last; value += 10) ticks.push(value);
    return ticks;
};

const getDateTicks = (start, end, count = 7) => {
    const startMs = dateValue(start);
    const endMs = dateValue(end);
    return Array.from({ length: count }, (_, index) => (
        startMs + (index / (count - 1)) * (endMs - startMs)
    ));
};

const ModelReleaseFrontierChart = ({ dataset, range = 'All', isZh = false }) => {
    const tooltipId = useId();
    const plotRef = useRef(null);
    const svgRef = useRef(null);
    const tooltipRef = useRef(null);
    const closeTimerRef = useRef(null);
    const hoveredPointRef = useRef(null);
    const tooltipHoveredRef = useRef(false);
    const [activeKey, setActiveKey] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState(null);

    const allRows = dataset?.series || [];
    const domainEnd = dataset?.asOf || dataset?.coverageEnd || allRows.at(-1)?.date;
    const domainStart = rangeStart(range, domainEnd, dataset?.coverageStart || allRows[0]?.date);
    const rows = allRows.filter((row) => row.date >= domainStart && row.date <= domainEnd);
    const plotRight = WIDTH - PAD.right;
    const plotBottom = HEIGHT - PAD.bottom;
    const plotWidth = plotRight - PAD.left;
    const plotHeight = plotBottom - PAD.top;
    const startMs = dateValue(domainStart);
    const endMs = dateValue(domainEnd);
    const xForDate = (date) => PAD.left + ((dateValue(date) - startMs) / (endMs - startMs || 1)) * plotWidth;

    const values = rows.map((row) => row.value);
    const rawMin = values.length ? Math.min(...values) : 0;
    const rawMax = values.length ? Math.max(...values) : 1;
    const valuePadding = Math.max(4, (rawMax - rawMin) * 0.08);
    const yMin = rawMin - valuePadding;
    const yMax = rawMax + valuePadding;
    const yForValue = (value) => PAD.top + ((yMax - value) / (yMax - yMin || 1)) * plotHeight;
    const yTicks = getAxisTicks(yMin, yMax);
    const xTicks = getDateTicks(domainStart, domainEnd, range === '1Y' ? 5 : 7);
    const points = rows.map((row) => ({
        id: `${row.date}:${row.model}`,
        row,
        x: xForDate(row.date),
        y: yForValue(row.value),
    }));
    const activePoint = points.find((point) => point.id === activeKey) || null;
    const activePointX = activePoint?.x;
    const activePointY = activePoint?.y;

    const cancelClose = () => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const closeTooltip = () => {
        cancelClose();
        hoveredPointRef.current = null;
        tooltipHoveredRef.current = false;
        setActiveKey(null);
        setTooltipPosition(null);
    };

    const scheduleClose = () => {
        cancelClose();
        closeTimerRef.current = window.setTimeout(() => {
            closeTimerRef.current = null;
            if (hoveredPointRef.current || tooltipHoveredRef.current || plotRef.current?.contains(document.activeElement)) return;
            setActiveKey(null);
            setTooltipPosition(null);
        }, TOOLTIP_CLOSE_DELAY);
    };

    const selectPoint = (id) => {
        cancelClose();
        setActiveKey(id);
        setTooltipPosition(null);
    };

    useEffect(() => () => {
        if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    }, []);

    useLayoutEffect(() => {
        if (!Number.isFinite(activePointX) || !Number.isFinite(activePointY)
            || !plotRef.current || !svgRef.current || !tooltipRef.current) return undefined;
        const plot = plotRef.current;
        const svg = svgRef.current;
        const tooltip = tooltipRef.current;
        const measure = () => {
            const svgRect = svg.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            if (!svgRect.width || !svgRect.height || !tooltipRect.width || !tooltipRect.height) return;
            const anchor = getSvgPointInPlot({
                svg,
                plot,
                pointX: activePointX,
                pointY: activePointY,
                viewBoxWidth: WIDTH,
                viewBoxHeight: HEIGHT,
            });
            const contained = getContainedTooltipPosition({
                anchorX: anchor.x - plot.scrollLeft,
                anchorY: anchor.y - plot.scrollTop,
                tooltipWidth: tooltipRect.width,
                tooltipHeight: tooltipRect.height,
                containerWidth: plot.clientWidth,
                containerHeight: plot.clientHeight || svgRect.height,
            });
            setTooltipPosition({
                left: plot.scrollLeft + contained.left,
                top: plot.scrollTop + contained.top,
            });
        };
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(svg);
        observer?.observe(tooltip);
        plot.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            plot.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
        };
    }, [activePointX, activePointY, range]);

    if (!rows.length) {
        return <div className="ai-dev-chart-empty">{isZh ? '当前范围没有可比 ECI 数据' : 'No comparable ECI data in this range'}</div>;
    }

    const formatDateTick = (tick) => new Intl.DateTimeFormat(isZh ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'short',
        timeZone: 'UTC',
    }).format(new Date(tick));

    return (
        <div className="ai-dev-release-chart ai-dev-eci-scatter">
            <div
                className="ai-dev-release-chart__plot"
                data-testid="model-release-chart-plot"
                ref={plotRef}
                onPointerLeave={(event) => {
                    if (!event.pointerType || event.pointerType === 'mouse') {
                        hoveredPointRef.current = null;
                        tooltipHoveredRef.current = false;
                        scheduleClose();
                    }
                }}
            >
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                    role="group"
                    aria-label={isZh ? '按发布日期展示的 Epoch 综合能力指数散点图' : 'Epoch Capabilities Index by release date'}
                >
                    <title>{isZh ? 'Epoch 综合能力指数（ECI）' : 'Epoch Capabilities Index (ECI)'}</title>
                    <desc>{isZh
                        ? `${rows.length} 个模型按发布日期和 ECI 分数展示。`
                        : `${rows.length} models plotted by release date and ECI score.`}</desc>

                    <text className="ai-dev-eci-scatter__axis-title" x={PAD.left} y="27">
                        {isZh ? 'ECI 分数' : 'ECI SCORE'}
                    </text>
                    <text className="ai-dev-eci-scatter__result-count" x={plotRight} y="27" textAnchor="end">
                        {isZh ? `${rows.length} 个结果` : `${rows.length} RESULTS`}
                    </text>

                    {yTicks.map((tick) => {
                        const y = yForValue(tick);
                        return (
                            <g key={`y:${tick}`}>
                                <line className="ai-dev-release-chart__grid" x1={PAD.left} x2={plotRight} y1={y} y2={y} />
                                <text className="ai-dev-release-chart__tick" x={PAD.left - 12} y={y + 4} textAnchor="end">{tick}</text>
                            </g>
                        );
                    })}

                    {xTicks.map((tick, index) => {
                        const x = PAD.left + (index / (xTicks.length - 1)) * plotWidth;
                        return (
                            <g key={`x:${tick}`}>
                                <line className="ai-dev-release-chart__grid ai-dev-eci-scatter__vertical-grid" x1={x} x2={x} y1={PAD.top} y2={plotBottom} />
                                <text className="ai-dev-release-chart__tick" x={x} y={plotBottom + 30} textAnchor={index === 0 ? 'start' : index === xTicks.length - 1 ? 'end' : 'middle'}>
                                    {formatDateTick(tick)}
                                </text>
                            </g>
                        );
                    })}

                    <text className="ai-dev-eci-scatter__x-title" x={PAD.left + plotWidth / 2} y={HEIGHT - 12} textAnchor="middle">
                        {isZh ? '发布日期' : 'RELEASE DATE'}
                    </text>

                    {points.map((point) => {
                        const group = ECI_ORGANIZATION_GROUPS[point.row.groupId] || ECI_ORGANIZATION_GROUPS.other;
                        const selected = activeKey === point.id;
                        return (
                            <g key={point.id}>
                                <circle
                                    className={`ai-dev-eci-scatter__point${selected ? ' is-active' : ''}`}
                                    cx={point.x}
                                    cy={point.y}
                                    r={point.row.highlight ? 5.2 : 4.5}
                                    fill={group.color}
                                    stroke={group.color}
                                />
                                {point.row.highlight ? (
                                    <text
                                        className="ai-dev-eci-scatter__label"
                                        x={point.x + point.row.highlight.dx}
                                        y={point.y + point.row.highlight.dy}
                                        fill={group.color}
                                        textAnchor={point.row.highlight.anchor}
                                    >
                                        {point.row.highlight.label}
                                    </text>
                                ) : null}
                                <g
                                    className="ai-dev-release-chart__hit"
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${point.row.date}, ${point.row.model}, ${point.row.organization}, ECI ${formatScore(point.row.value)}, ${point.row.confidence}`}
                                    aria-describedby={selected ? tooltipId : undefined}
                                    onPointerEnter={(event) => {
                                        if (!event.pointerType || event.pointerType === 'mouse') {
                                            hoveredPointRef.current = point.id;
                                            selectPoint(point.id);
                                        }
                                    }}
                                    onPointerLeave={(event) => {
                                        if (!event.pointerType || event.pointerType === 'mouse') {
                                            if (hoveredPointRef.current === point.id) hoveredPointRef.current = null;
                                            scheduleClose();
                                        }
                                    }}
                                    onPointerUp={(event) => {
                                        if (event.pointerType !== 'mouse') {
                                            if (selected) closeTooltip();
                                            else selectPoint(point.id);
                                        }
                                    }}
                                    onFocus={() => selectPoint(point.id)}
                                    onBlur={(event) => {
                                        if (!plotRef.current?.contains(event.relatedTarget)) closeTooltip();
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Escape') closeTooltip();
                                    }}
                                >
                                    <circle className="ai-dev-release-chart__hit-area" cx={point.x} cy={point.y} r={HIT_RADIUS} />
                                </g>
                            </g>
                        );
                    })}

                    <g className="ai-dev-eci-scatter__legend" transform={`translate(${plotRight + 24} 54)`}>
                        <text className="ai-dev-eci-scatter__legend-title" x="0" y="0">{isZh ? '机构' : 'ORGANIZATION'}</text>
                        {Object.entries(ECI_ORGANIZATION_GROUPS).map(([id, group], index) => (
                            <g key={id} transform={`translate(0 ${24 + index * 25})`}>
                                <rect x="0" y="-10" width="11" height="11" fill={group.color} />
                                <text x="19" y="0">{group.label}</text>
                            </g>
                        ))}
                    </g>
                </svg>

                {activePoint ? (
                    <div
                        ref={tooltipRef}
                        className="ai-dev-release-chart__tooltip"
                        style={{
                            '--ai-tooltip-color': ECI_ORGANIZATION_GROUPS[activePoint.row.groupId]?.color || ECI_ORGANIZATION_GROUPS.other.color,
                            left: tooltipPosition ? `${tooltipPosition.left}px` : 0,
                            top: tooltipPosition ? `${tooltipPosition.top}px` : 0,
                        }}
                        onPointerEnter={(event) => {
                            if (!event.pointerType || event.pointerType === 'mouse') {
                                tooltipHoveredRef.current = true;
                                cancelClose();
                            }
                        }}
                        onPointerLeave={(event) => {
                            if (!event.pointerType || event.pointerType === 'mouse') {
                                tooltipHoveredRef.current = false;
                                scheduleClose();
                            }
                        }}
                    >
                        <div id={tooltipId} role="tooltip" className="ai-dev-release-chart__tooltip-content">
                            <time>{activePoint.row.date}</time>
                            <strong>{activePoint.row.model}</strong>
                            <span>{activePoint.row.organization}</span>
                            <b>ECI {formatScore(activePoint.row.value)}</b>
                            <small>{isZh ? `置信度：${activePoint.row.confidence}` : `Confidence: ${activePoint.row.confidence}`}</small>
                        </div>
                        <div className="ai-dev-release-chart__tooltip-actions">
                            <a href={dataset.sourceUrl} target="_blank" rel="noreferrer">Epoch ECI</a>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default ModelReleaseFrontierChart;
